import React, { useRef, useState, useEffect } from "react";
import { TextField, Button, Typography } from "@mui/material";
import TextToolsPanel from "../widgets/TextToolsPanel";
import { extractNumberedVariables } from "../utils/util";
import _ from "lodash";
import TextFieldVarValues from "./TextFieldVarValues";
function TextFieldVariable({
  variableType = "Number",
  variableRequired = true,
  rows = 4,
  label = "Type Something",
  value: propValue,
  onChange,
  defaultValue = "",
  onParamChange,
  varLimitCount = 10,
  charLimit = 1024,
  disabled = false,
}) {
  const textFieldRef = useRef(null);
  const [params, setParams] = useState([]);
  const isControlled = propValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const value = isControlled ? propValue : internalValue;
  const safeCharLimit = Math.max(10, charLimit);

  useEffect(() => {
    if (!isControlled) {
      setInternalValue(defaultValue);
    }
  }, [defaultValue, isControlled]);

  const insertItems = (ch, pos) => {
    if (textFieldRef.current) {
      const input = textFieldRef.current;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const scrollP = input.scrollTop;
      const newValue = value.substring(0, start) + ch + value.substring(end);

      if (isControlled) {
        onChange?.(newValue);
      } else {
        setInternalValue(newValue);
        onChange?.(newValue);
      }

      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + pos, start + pos);
        input.scrollTop = scrollP;
      }, 0);
    }
  };

  useEffect(() => {
    onParamChange?.(params);
  }, [params]);

  useEffect(() => {
    if (!variableRequired) {
      return;
    }
    const newSa = [...new Set(extractNumberedVariables(value))].sort(
      (a, b) => a - b
    );
    const keys = params.map((item) => item.key);
    setParams((prev) =>
      newSa.map((item) =>
        keys.includes(item)
          ? prev.find((p) => p.key === item)
          : { key: item, value: "" }
      )
    );
  }, [value, variableRequired]);

  const handleInputChange = (event) => {
    const newValue = event.target.value;
    if (isControlled) {
      onChange?.(newValue);
    } else {
      setInternalValue(newValue);
      onChange?.(newValue);
    }
  };

  const len = value.length;
  const helper =
    `${len}/${safeCharLimit}` +
    (len === safeCharLimit
      ? " – limit reached"
      : len >= safeCharLimit - 10
      ? " – approaching"
      : "");

  return (
    <>
      <TextField
        inputRef={textFieldRef}
        label={label}
        value={value}
        multiline
        rows={rows}
        disabled={disabled}
        inputProps={{ maxLength: safeCharLimit }}
        error={len === safeCharLimit}
        helperText={
          <Typography
            variant="caption"
            sx={{
              color:
                len === safeCharLimit
                  ? "error.main"
                  : len >= safeCharLimit - 10
                  ? "warning.main"
                  : "text.secondary",
            }}
          >
            {helper}
          </Typography>
        }
        onChange={handleInputChange}
        variant="outlined"
        fullWidth
        margin="normal"
      />
      <TextToolsPanel
        variableRequired={variableRequired}
        disableVar={params.length >= varLimitCount}
        disablePanel={disabled}
        cIndex={params.length + 1}
        onInsertChar={(ch, pos) => {
          insertItems(ch, pos);
        }}
        conProps={{ mt: -2 }}
      />
      <TextFieldVarValues params={params} setParams={setParams} />
    </>
  );
}

export default TextFieldVariable;
